import React from "react";
import { AlertTriangle, Info } from "lucide-react";
import Modal from "../../../shared/ui/Modal";
import Tabs, { Tab } from "../../../shared/ui/Tabs";

import { useAuth } from "../../auth/AuthContext";
import { useItemForm } from "../model/ItemFormContext";
import { findAutoMyCompareItemId } from "../model/my-compare-item";
import { fetchCompareItemUrlData, getCompareUrlData } from "../compare.api";
import { fetchProcessReadiness } from "../../../api/account";
import { getErrorMessage } from "../../../shared/api/helpers";
import { parseMyCompareItemId } from "./parseMyCompareItemId";
import { isDirectCompareItemUrl } from "./directCompareUrl";
import {
  canDisplayStaleCompareItemSearch,
  CompareItemSearchCriteria,
  isSameCompareItemSearchCriteria,
  mergeVisibleCompareItems,
  placeDetailItemsLast,
  parseCompareItemSearchCache,
} from "./compareItemSearchModal.utils";

interface Props {
  show: boolean;
  onHide: () => void;
}

type SearchResultState = "empty" | "fresh" | "cached" | "legacy" | "stale";
type ItemTab = "my" | "specified" | "prevented";
const emptyItemUrls = (): Record<ItemTab, string> => ({ my: "", specified: "", prevented: "" });
const defaultLookupPrice = (): Record<ItemTab, boolean> => ({ my: false, specified: false, prevented: false });

/*
- 모달이 열리면, 이전에 저장된 검색 결과와 선택된 상품 정보를 불러와서 화면에 복원합니다.
- 사용자가 “검색” 버튼을 누르면 현재 비교 URL 기준으로 상품 목록을 조회하고, 결과를 테이블로 표시합니다.
- 하단에는 3개 탭이 있습니다.
  - 내 상품 지정: 1개만 선택 가능
  - 경쟁상품 지정: 구독의 지정상품 제한까지
  - 경쟁방지상품 지정: 최대 10개
- 같은 상품은 서로 다른 탭에서 중복 선택되지 않도록 제어합니다.
- 검색 결과가 바뀌어도 현재 모달에서 선택한 항목은 유지됩니다.
- 저장하면 선택한 상품 정보를 폼 상태에 반영하고 모달을 닫고, 닫기만 하면 변경사항은 반영되지 않습니다.
*/

const CompareItemSearchModal: React.FC<Props> = ({ show, onHide }) => {
  const auth = useAuth();
  const specifiedItemLimit = auth.subscriptionStatus?.specified_item_limit ?? 3;
  const { watch, setValue, getValues, shop } = useItemForm();

  // Watch form values
  const compare_site = watch("compare_site");
  const compare_url = watch("compare_url");
  const compare_column = watch("compare_column");
  const compare_pages = watch("compare_pages");
  const compare_range = watch("compare_range");
  const specifiedFieldName = compare_site === "direct"
    ? "compare_items_direct_specified" as const
    : "compare_items_specified" as const;
  const myItemIdFieldName = compare_site === "direct"
    ? "my_direct_compare_item_id" as const
    : "my_compare_item_id" as const;
  const myItemFieldName = compare_site === "direct"
    ? "my_direct_compare_item" as const
    : "my_compare_item" as const;
  const include_shipping_fee = watch("include_shipping_fee");
  const displayItemPrice = (item: any): number | null => {
    if (compare_site === "direct" && typeof item.item_price_on_page === "number") {
      return item.item_price_on_page - (item.item_discount || 0)
        - (item.item_discount_by_market || 0)
        + (include_shipping_fee ? (item.item_shipping_fee || 0) : 0);
    }
    return item.item_price ?? null;
  };
  const extra_config = watch("extra_config");
  const shop_site = shop?.market_site;

  // Shared items list
  const [items, setItems] = React.useState<any[]>([]);

  // Tab-specific checked items
  const [myCheckedItems, setMyCheckedItems] = React.useState<Set<string>>(
    new Set(),
  );
  const [specifiedCheckedItems, setSpecifiedCheckedItems] = React.useState<
    Set<string>
  >(new Set());
  const [preventedCheckedItems, setPreventedCheckedItems] = React.useState<
    Set<string>
  >(new Set());

  const [activeTab, setActiveTab] = React.useState<string>("my");
  const [errorMessage, setErrorMessage] = React.useState<string>("");
  const [urlCopyStatus, setUrlCopyStatus] = React.useState<{ itemId: string; success: boolean } | null>(null);
  const [processMessage, setProcessMessage] = React.useState<string>("");
  const [itemUrls, setItemUrls] = React.useState(emptyItemUrls);
  const [itemUrlErrors, setItemUrlErrors] = React.useState(emptyItemUrls);
  const [fetchingItemUrlTab, setFetchingItemUrlTab] = React.useState<ItemTab | null>(null);
  const [lookupPrice, setLookupPrice] = React.useState(defaultLookupPrice);
  const [isSearching, setIsSearching] = React.useState<boolean>(false);
  const [searchResultState, setSearchResultState] =
    React.useState<SearchResultState>("empty");
  const formSnapshotRef = React.useRef<Record<string, any> | null>(null);
  const itemCacheRef = React.useRef<Map<string, any>>(new Map());
  const modalSessionRef = React.useRef(0);

  const parseCompareItemsValue = (value: any) => {
    try {
      if (!value) {
        return [];
      }

      let itemsData = value;
      if (typeof value === "string") {
        itemsData = JSON.parse(value);
      }

      return Array.isArray(itemsData) ? itemsData : [];
    } catch (error) {
      return [];
    }
  };

  const getCurrentSearchCriteria = (
    pages = Number(compare_pages || 1),
  ): CompareItemSearchCriteria => ({
    compare_site: String(compare_site || ""),
    compare_url: String(compare_url || ""),
    compare_column: String(compare_column || ""),
    compare_pages: pages,
    compare_range: String(compare_range || ""),
    include_shipping_fee: Boolean(include_shipping_fee),
    shop_site: String(shop_site || ""),
  });

  const cacheItems = (itemsToCache: any[]) => {
    itemsToCache.forEach((item) => {
      const itemId = String(item?.item_id ?? "");
      if (itemId) {
        const cachedMemo = itemCacheRef.current.get(itemId)?.memo;
        itemCacheRef.current.set(
          itemId,
          cachedMemo !== undefined && item.memo === undefined
            ? { ...item, memo: cachedMemo }
            : item,
        );
      }
    });
  };

  const getCachedItems = (itemIds: Set<string>) => {
    return Array.from(itemIds)
      .map((itemId) => itemCacheRef.current.get(itemId))
      .filter(Boolean);
  };

  const getVisibleItemsForTab = (tab: string, baseItems: any[] = items) => {
    const savedSpecifiedItems = parseCompareItemsValue(
      getValues(specifiedFieldName),
    );
    const savedPreventedItems = parseCompareItemsValue(
      getValues("compare_items_prevented"),
    );
    const selectedItems = getCachedItems(
      tab === "my"
        ? myCheckedItems
        : tab === "specified"
          ? specifiedCheckedItems
          : preventedCheckedItems,
    );
    const selectedIds = new Set(selectedItems.map((item) => String(item.item_id)));

    if (tab === "specified") {
      return placeDetailItemsLast(mergeVisibleCompareItems(
        baseItems,
        savedSpecifiedItems.filter((item) => !selectedIds.has(String(item.item_id))),
        selectedItems,
      ));
    }

    if (tab === "prevented") {
      return placeDetailItemsLast(mergeVisibleCompareItems(
        baseItems,
        savedPreventedItems.filter((item) => !selectedIds.has(String(item.item_id))),
        selectedItems,
      ));
    }

    return placeDetailItemsLast(mergeVisibleCompareItems(baseItems, selectedItems));
  };

  const getAllPersistableItems = () => {
    const savedSpecifiedItems = parseCompareItemsValue(
      getValues(specifiedFieldName),
    );
    const savedPreventedItems = parseCompareItemsValue(
      getValues("compare_items_prevented"),
    );
    const selectedItemIds = new Set([
      ...myCheckedItems,
      ...specifiedCheckedItems,
      ...preventedCheckedItems,
    ]);
    const selectedItems = getCachedItems(selectedItemIds);

    return mergeVisibleCompareItems(
      items,
      selectedItems,
      savedSpecifiedItems,
      savedPreventedItems,
    );
  };

  const autoAssignMyItemIfNeeded = (sourceItems: any[], existingMyItemId = "") => {
    if (!sourceItems || sourceItems.length === 0) {
      return;
    }

    if (existingMyItemId) {
      return;
    }

    const autoMyItemId = findAutoMyCompareItemId(sourceItems);

    if (autoMyItemId) {
      setMyCheckedItems(new Set([autoMyItemId]));
      setValue(myItemIdFieldName, autoMyItemId, { shouldValidate: true });
    } else {
      console.debug(`No item matched for auto-assigning my item. compare_site: ${compare_site}, shopItemId: ${getValues("shop_item_id")}, shopItemImageUrl: ${getValues("shop_item_image_url")}`);
    }
  };

  const handleComparePagesChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const nextComparePages = Number(event.target.value);
    setValue("compare_pages", nextComparePages, {
      shouldDirty: true,
      shouldValidate: true,
    });
    const cachedSearch = parseCompareItemSearchCache(
      getValues("compare_items_searched"),
    );
    const nextCriteria = getCurrentSearchCriteria(nextComparePages);
    setSearchResultState(
      cachedSearch.items.length === 0
        ? "empty"
        : cachedSearch.criteria === null
          ? "legacy"
          : isSameCompareItemSearchCriteria(cachedSearch.criteria, nextCriteria)
            ? "cached"
            : "stale",
    );
  };

  const handleModalOpen = () => {
    const formValues = getValues() as Record<string, any>;
    formSnapshotRef.current = {
      compare_items_specified: formValues.compare_items_specified,
      compare_items_direct_specified: formValues.compare_items_direct_specified,
      compare_items_prevented: formValues.compare_items_prevented,
      my_compare_item_id: formValues.my_compare_item_id,
      my_compare_item: formValues.my_compare_item,
      my_direct_compare_item_id: formValues.my_direct_compare_item_id,
      my_direct_compare_item: formValues.my_direct_compare_item,
      found_price_exposed: formValues.found_price_exposed,
      found_price_ranked: formValues.found_price_ranked,
    };
    modalSessionRef.current += 1;
    setIsSearching(false);
    setFetchingItemUrlTab(null);

    let cachedItems: any[] = [];
    itemCacheRef.current.clear();

    // Load last searched items
    const cachedSearch = parseCompareItemSearchCache(
      formValues.compare_items_searched,
    );
    const currentCriteria = getCurrentSearchCriteria();
    const canDisplayCachedItems =
      cachedSearch.criteria === null ||
      canDisplayStaleCompareItemSearch(cachedSearch.criteria, currentCriteria);
    cachedItems = compare_site === "direct" ? [] : canDisplayCachedItems ? cachedSearch.items : [];
    cacheItems(parseCompareItemsValue(formValues[specifiedFieldName]));
    cacheItems(parseCompareItemsValue(formValues.compare_items_prevented));
    if (formValues[myItemFieldName]?.item_id) cacheItems([formValues[myItemFieldName]]);
    cacheItems(cachedItems);
    if (compare_site !== "direct" && canDisplayCachedItems) cacheItems(cachedSearch.manual_items);
    setItems(cachedItems);

    if (compare_site === "direct" || cachedSearch.items.length === 0) {
      setSearchResultState("empty");
    } else if (cachedSearch.criteria === null) {
      setSearchResultState("legacy");
    } else if (
      isSameCompareItemSearchCriteria(cachedSearch.criteria, currentCriteria)
    ) {
      setSearchResultState("cached");
    } else {
      setSearchResultState("stale");
    }

    // Load saved items for each tab
    const my_compare_item_id = String(getValues(myItemIdFieldName) || "").trim();
    try {
      if (my_compare_item_id) {
        setMyCheckedItems(new Set([my_compare_item_id]));
      } else {
        setMyCheckedItems(new Set());
      }
    } catch (error) {
      setMyCheckedItems(new Set());
    }

    const compare_items_specified = getValues(specifiedFieldName);
    try {
      if (compare_items_specified) {
        let items_data = compare_items_specified;
        if (typeof compare_items_specified === "string") {
          items_data = JSON.parse(compare_items_specified);
        }
        const ids = Array.isArray(items_data)
          ? items_data.map((item: any) => String(item.item_id))
          : [];
        setSpecifiedCheckedItems(new Set(ids));
      } else {
        setSpecifiedCheckedItems(new Set());
      }
    } catch (error) {
      setSpecifiedCheckedItems(new Set());
    }

    const compare_items_prevented = getValues("compare_items_prevented");
    try {
      if (compare_items_prevented) {
        let items_data = compare_items_prevented;
        if (typeof compare_items_prevented === "string") {
          items_data = JSON.parse(compare_items_prevented);
        }
        const ids = Array.isArray(items_data)
          ? items_data.map((item: any) => String(item.item_id))
          : [];
        setPreventedCheckedItems(new Set(ids));
      } else {
        setPreventedCheckedItems(new Set());
      }
    } catch (error) {
      setPreventedCheckedItems(new Set());
    }

    setErrorMessage("");
    setProcessMessage("");
    setItemUrls(emptyItemUrls());
    setItemUrlErrors(emptyItemUrls());
    setLookupPrice(defaultLookupPrice());
    setActiveTab("my");

    // 캐시된 리스트가 표시될 때도, 내상품 미지정이면 자동 지정 시도
    autoAssignMyItemIfNeeded(cachedItems, my_compare_item_id);
  };

  const handleModalClose = ({ restore = true }: { restore?: boolean } = {}) => {
    if (restore && formSnapshotRef.current) {
      Object.entries(formSnapshotRef.current).forEach(([field, value]) => {
        setValue(field as any, value);
      });
    }

    formSnapshotRef.current = null;
    modalSessionRef.current += 1;
    setItems([]);
    setErrorMessage("");
    setProcessMessage("");
    setItemUrls(emptyItemUrls());
    setItemUrlErrors(emptyItemUrls());
    setIsSearching(false);
    setFetchingItemUrlTab(null);
    setSearchResultState("empty");
    onHide();
  };

  const handleSearchSumbit = async () => {
    if (isSearching) {
      // 이미 검색 중이면 중복 요청을 막는다.
      // (버튼 연타/엔터 중복 입력으로 인한 race 방지)
      return;
    }

    const readiness = await fetchProcessReadiness();
    if (!readiness?.ready) {
      setErrorMessage(
        readiness?.errors[0]?.message
          || "죄송합니다. 사용자 전용 환경을 준비 중입니다. 관리자에게 문의해주세요."
      );
      return;
    }

    // 검색 시작: UI 잠금용 플래그 ON
    setIsSearching(true);
    const searchSession = modalSessionRef.current;
    getCompareUrlData({
      auth,
      compare_site: compare_site,
      compare_url: compare_url,
      compare_column: compare_column,
      compare_pages: Number(compare_pages),
      compare_range: compare_range,
      include_shipping_fee: Boolean(include_shipping_fee),
      extra_config,
      shop_site: shop_site,
      shop_id: shop?.id,
      shop_item_id: String(getValues("shop_item_id") || ""),
      shop_item_image_url: String(getValues("shop_item_image_url") || ""),
      setErrorMessage: (message) => {
        if (searchSession === modalSessionRef.current) {
          setErrorMessage(message);
        }
      },
      setProcessMessage: (message) => {
        if (searchSession === modalSessionRef.current) {
          setProcessMessage(message || "");
        }
      },
      setResult: (envelop: any) => {
        if (searchSession !== modalSessionRef.current) {
          return;
        }

        // 서버 응답에서 아이템 목록을 추출해 화면 테이블을 갱신한다.
        const data = envelop.data;
        const newItems = data.items || [];
        cacheItems(newItems);
        setItems(newItems);
        setSearchResultState("fresh");

        // "마지막 검색 결과"를 폼에 저장해 모달 재오픈 시 캐시처럼 재사용한다.
        const previousSearch = parseCompareItemSearchCache(getValues("compare_items_searched"));
        const manualItems = previousSearch.criteria === null ||
          canDisplayStaleCompareItemSearch(previousSearch.criteria, getCurrentSearchCriteria())
          ? previousSearch.manual_items : [];
        setValue("compare_items_searched", {
          items: newItems,
          manual_items: manualItems,
          criteria: getCurrentSearchCriteria(),
          searched_at: new Date().toISOString(),
        });

        // 검색 결과 부가값(노출가/목표가)이 있으면 폼에 반영한다.
        if (data.price_exposed) {
          setValue("found_price_exposed" as any, data.price_exposed);
        }
        if (data.target_price_exposed) {
          setValue("found_price_ranked" as any, data.target_price_exposed);
        }

        // 내상품이 비어 있으면 비교 사이트별 규칙으로 자동 지정 시도한다.
        const currentMyItemId =
          myCheckedItems.size > 0
            ? String(Array.from(myCheckedItems)[0] || "")
            : "";
        autoAssignMyItemIfNeeded(newItems, currentMyItemId);

        // 검색 완료: UI 잠금 해제
        setIsSearching(false);
      },
    }).finally(() => {
      // 성공/실패와 무관하게 검색 플래그를 확실히 해제한다.
      if (searchSession === modalSessionRef.current) {
        setIsSearching(false);
      }
    });
  };

  const handleItemToggle = (itemId: string, tab: string) => {
    setErrorMessage("");
    // 다른 탭에서 이미 선택된 아이템인지 확인 (3개 탭의 선택이 상호배타적)
    const otherCheckedIds = new Set<string>();

    if (tab !== "my") {
      myCheckedItems.forEach((id) => otherCheckedIds.add(id));
    }
    if (tab !== "specified") {
      specifiedCheckedItems.forEach((id) => otherCheckedIds.add(id));
    }
    if (tab !== "prevented") {
      preventedCheckedItems.forEach((id) => otherCheckedIds.add(id));
    }

    // 다른 탭에서 이미 선택된 아이템은 추가 불가
    if (
      otherCheckedIds.has(itemId) &&
      !(
        (tab === "my" && myCheckedItems.has(itemId)) ||
        (tab === "specified" && specifiedCheckedItems.has(itemId)) ||
        (tab === "prevented" && preventedCheckedItems.has(itemId))
      )
    ) {
      return;
    }

    if (tab === "my") {
      if (myCheckedItems.has(itemId)) {
        myCheckedItems.delete(itemId);
        setMyCheckedItems(new Set(myCheckedItems));
      } else {
        // 내상품 지정은 1개만 선택 가능
        // 다른 탭에서 선택되지 않았다면 선택
        if (
          !specifiedCheckedItems.has(itemId) &&
          !preventedCheckedItems.has(itemId)
        ) {
          setMyCheckedItems(new Set([itemId]));
        }
      }
    } else if (tab === "specified") {
      if (specifiedCheckedItems.has(itemId)) {
        specifiedCheckedItems.delete(itemId);
        setSpecifiedCheckedItems(new Set(specifiedCheckedItems));
      } else {
        // 경쟁지정 상품은 구독의 지정상품 제한까지 선택 가능
        // 다른 탭에서 선택되지 않았다면 선택
        if (!myCheckedItems.has(itemId) && !preventedCheckedItems.has(itemId)) {
          if (specifiedCheckedItems.size < specifiedItemLimit) {
            specifiedCheckedItems.add(itemId);
            setSpecifiedCheckedItems(new Set(specifiedCheckedItems));
          } else {
            setErrorMessage(`지정상품은 최대 ${specifiedItemLimit}개까지 선택할 수 있습니다.`);
          }
        }
      }
    } else if (tab === "prevented") {
      if (preventedCheckedItems.has(itemId)) {
        preventedCheckedItems.delete(itemId);
        setPreventedCheckedItems(new Set(preventedCheckedItems));
      } else {
        // 경쟁방지 상품은 10개까지 선택 가능
        // 다른 탭에서 선택되지 않았다면 선택
        if (!myCheckedItems.has(itemId) && !specifiedCheckedItems.has(itemId)) {
          if (preventedCheckedItems.size < 10) {
            preventedCheckedItems.add(itemId);
            setPreventedCheckedItems(new Set(preventedCheckedItems));
          }
        }
      }
    }
  };

  const handleItemMemoChange = (item: any, memo: string) => {
    const itemId = String(item.item_id);
    itemCacheRef.current.set(itemId, {
      ...item,
      ...itemCacheRef.current.get(itemId),
      memo,
    });
    setItems((previous) => [...previous]);
  };

  const handleItemUrlSelect = async (tab: ItemTab) => {
    if (fetchingItemUrlTab || isSearching) return;
    const session = modalSessionRef.current;
    const itemUrl = itemUrls[tab].trim();
    const setTabError = (message: string) =>
      setItemUrlErrors((previous) => ({ ...previous, [tab]: message }));
    if (!itemUrl) {
      setTabError("상품 URL을 입력하세요.");
      return;
    }

    try {
      if (compare_site === "direct" && !isDirectCompareItemUrl(itemUrl, tab !== "my")) {
        setTabError(tab === "my" ? "내 상품은 일반 상품 상세 URL로 지정하세요." : "유효한 상품 URL이 아닙니다.");
        return;
      }
      const itemId = compare_site === "direct" ? itemUrl : parseMyCompareItemId(itemUrl, compare_site);
      const selectedByOtherTab =
        (tab !== "my" && myCheckedItems.has(itemId)) ||
        (tab !== "specified" && specifiedCheckedItems.has(itemId)) ||
        (tab !== "prevented" && preventedCheckedItems.has(itemId));
      if (selectedByOtherTab) {
        setTabError("다른 탭에서 이미 선택된 상품입니다.");
        return;
      }

      const checkedItems = tab === "my" ? myCheckedItems
        : tab === "specified" ? specifiedCheckedItems : preventedCheckedItems;
      if (checkedItems.has(itemId)) {
        setTabError("이미 지정된 상품입니다.");
        return;
      }
      const limit = tab === "my" ? 1 : tab === "specified" ? specifiedItemLimit : 10;
      if (checkedItems.size >= limit) {
        setTabError(`최대 ${limit}건까지 선택 가능합니다.`);
        return;
      }

      const cachedItem = itemCacheRef.current.get(itemId);
      if (!items.some((item) => String(item.item_id) === itemId)) {
        if (!lookupPrice[tab]) {
          if (!cachedItem) cacheItems([{ item_id: itemId, item_url: itemUrl }]);
        } else {
          setFetchingItemUrlTab(tab);
          const detail = await fetchCompareItemUrlData({
            compare_item_url: itemUrl,
            compare_site,
            shop_site,
            shop_id: shop?.id,
          });
          if (session !== modalSessionRef.current) return;
          const directPrice = detail.price - (detail.discount || 0) - (detail.discount_by_market || 0);
          cacheItems([{
            item_id: itemId,
            item_url: itemUrl,
            dest_url: detail.url,
            item_title: detail.title?.trim() || (compare_site === "direct" ? "" : `상품 ${itemId}`),
            mall_seller_name: detail.mall_seller_name?.trim() || "",
            item_price: compare_site === "direct"
              ? directPrice + (include_shipping_fee ? (detail.shipping_fee || 0) : 0)
              : detail.price_sale || detail.price,
            item_price_sale_on_page: detail.price_sale,
            item_price_on_page: detail.price,
            item_discount: detail.discount,
            item_discount_by_market: detail.discount_by_market,
            item_shipping_fee: detail.shipping_fee,
            item_delivery: detail.shipping_fee,
            item_price_source: "detail",
          }]);
        }
      }
      const nextCheckedItems = tab === "my" ? new Set([itemId]) : new Set([...checkedItems, itemId]);
      if (tab === "my") setMyCheckedItems(nextCheckedItems);
      else if (tab === "specified") setSpecifiedCheckedItems(nextCheckedItems);
      else setPreventedCheckedItems(nextCheckedItems);
      setItemUrls((previous) => ({ ...previous, [tab]: "" }));
      setTabError("");
    } catch (error: any) {
      if (session !== modalSessionRef.current) return;
      if (error?.response?.status === 401) await auth?.logout();
      setTabError(
        error?.response?.data?.details
        || (error?.isAxiosError ? getErrorMessage(error) : error?.message)
        || "URL 처리 중 오류가 발생했습니다.",
      );
    } finally {
      if (session === modalSessionRef.current) setFetchingItemUrlTab(null);
    }
  };

  const renderUrlSelector = (tab: ItemTab) => (
    <>
      <div className="d-flex align-items-center gap-2 mt-3">
        <input
          type="url"
          className="form-control"
          aria-label="상품 URL"
          placeholder="상품 URL을 입력하세요"
          value={itemUrls[tab]}
          disabled={!!fetchingItemUrlTab}
          onChange={(event) => {
            setItemUrls((previous) => ({ ...previous, [tab]: event.target.value }));
            setItemUrlErrors((previous) => ({ ...previous, [tab]: "" }));
          }}
        />
        <button type="button" className="btn btn-outline-primary text-nowrap" disabled={!!fetchingItemUrlTab || isSearching} onClick={() => void handleItemUrlSelect(tab)}>
          {fetchingItemUrlTab === tab ? "상품 조회 중..." : "URL로 지정"}
        </button>
        <label className="form-check text-nowrap">
          <input type="checkbox" className="form-check-input" checked={lookupPrice[tab]}
            onChange={(event) => setLookupPrice((previous) => ({ ...previous, [tab]: event.target.checked }))} />
          조회
        </label>
      </div>
      {itemUrlErrors[tab] && <div className="text-danger mt-2" role="alert">{itemUrlErrors[tab]}</div>}
    </>
  );

  const handleSaveAll = () => {
    if (compare_range === "items_specified" && specifiedCheckedItems.size > specifiedItemLimit) {
      setErrorMessage(`지정상품은 최대 ${specifiedItemLimit}개까지 선택할 수 있습니다.`);
      return;
    }
    const persistableItems = getAllPersistableItems();
    const cachedSearch = parseCompareItemSearchCache(getValues("compare_items_searched"));
    const selectedIds = new Set([
      ...myCheckedItems, ...specifiedCheckedItems, ...preventedCheckedItems,
    ]);
    setValue("compare_items_searched", compare_site === "direct" ? {} : {
      ...cachedSearch,
      manual_items: Array.from(selectedIds)
        .map((itemId) => itemCacheRef.current.get(itemId))
        .filter((item) => item?.item_url
          && !cachedSearch.items.some((searchedItem) => String(searchedItem.item_id) === String(item.item_id))),
    });

    // 내 상품 아이템 ID
    const myItemId = myCheckedItems.size > 0
      ? String(Array.from(myCheckedItems)[0] || "")
      : "";
    setValue(myItemIdFieldName, myItemId, { shouldValidate: true });
    const withMemo = (item: any) => {
      const memo = itemCacheRef.current.get(String(item.item_id))?.memo;
      return memo === undefined ? item : { ...item, memo: String(memo).trim() };
    };
    setValue(myItemFieldName, myItemId ? withMemo(itemCacheRef.current.get(myItemId) || { item_id: myItemId }) : {});

    // 경쟁지정 아이템들
    const specifiedSelectedItems = placeDetailItemsLast(persistableItems.filter((item) =>
      specifiedCheckedItems.has(String(item.item_id)),
    ).map(withMemo));
    setValue(specifiedFieldName, specifiedSelectedItems);
    if (compare_site === "direct") {
      setValue("extra_config", {
        ...(getValues("extra_config") || {}),
        direct_specified_initialized: true,
      });
    }

    // 경쟁방지 아이템들
    const preventedSelectedItems = placeDetailItemsLast(persistableItems.filter((item) =>
      preventedCheckedItems.has(String(item.item_id)),
    ).map(withMemo));
    if (compare_site !== "direct") {
      setValue("compare_items_prevented" as any, preventedSelectedItems);
    }

    handleModalClose({ restore: false });
  };

  const renderItemsTable = (checkedSet: Set<string>, maxItems: number) => {
    const visibleItems = getVisibleItemsForTab(activeTab);

    // 다른 탭에서 선택된 아이템들
    const allOtherCheckedIds = new Set([
      ...myCheckedItems,
      ...specifiedCheckedItems,
      ...preventedCheckedItems,
    ]);

    const getBadgeInfo = (itemId: string) => {
      if (activeTab !== "my" && myCheckedItems.has(itemId)) {
        return { show: true, label: "내상품", variant: "primary" };
      }
      if (activeTab !== "specified" && specifiedCheckedItems.has(itemId) && compare_range === "items_specified") {
        return { show: true, label: "경쟁지정", variant: "success" };
      }
      if (activeTab !== "prevented" && preventedCheckedItems.has(itemId)) {
        return { show: true, label: "경쟁방지", variant: "danger" };
      }
      return { show: false };
    };

    return (
      <>
        {visibleItems && visibleItems.length > 0 && (
          <div className="table-responsive mt-3">
            {activeTab !== "my" && (
              <div className="alert alert-info mb-2 p-2">
                <small>
                  최대 {maxItems}건까지 선택 가능합니다. 현재 선택된 항목:{" "}
                  <strong>{checkedSet.size}</strong>/{maxItems}
                </small>
              </div>
            )}

            <table
              className="table table-bordered"
              style={{ tableLayout: "fixed" }}
            >
              <colgroup>
                <col style={{ width: "40px" }} />
                <col style={{ width: "150px" }} />
                <col />
                {compare_site == "danawa" && <col style={{ width: "60px" }} />}
                <col style={{ width: "100px" }} />
              </colgroup>
              <thead>
                <tr>
                  <th scope="col"></th>
                  <th scope="col" className="text-nowrap">
                    판매자
                  </th>
                  <th scope="col">상품명</th>
                  {compare_site == "danawa" && (
                    <th scope="col" className="text-nowrap">
                      컬럼
                    </th>
                  )}
                  <th scope="col" className="text-nowrap">
                    가격
                  </th>
                </tr>
              </thead>
              <tbody>
                {visibleItems.map((item, index) => {
                  const itemId = String(item.item_id);
                  const isCheckedInOtherTab =
                    allOtherCheckedIds.has(itemId) &&
                    !checkedSet.has(itemId);
                  const isDisabled = isCheckedInOtherTab;

                  return (
                    <tr key={index}>
                      <td className="text-center">
                        <input
                          type="checkbox"
                          name="compare-item-id"
                          value={item.item_id}
                          checked={checkedSet.has(itemId)}
                          disabled={isDisabled}
                          title={
                            isCheckedInOtherTab
                              ? "다른 탭에서 이미 선택되었습니다"
                              : ""
                          }
                          onChange={() =>
                            handleItemToggle(itemId, activeTab)
                          }
                        />
                      </td>
                      <td
                        className="text-truncate"
                        title={item.mall_seller_name}
                      >
                        {item.mall_seller_name || "-"}
                      </td>
                      <td className="text-start" title={item.item_title}>
                        {(() => {
                          const badge = getBadgeInfo(itemId);
                          return (
                            <>
                              {badge.show && (
                                <span className={`badge bg-${badge.variant} me-2`}>
                                  {badge.label}
                                </span>
                              )}
                              {item.item_url ? (
                                <a href={item.item_url} target="_blank" rel="noopener noreferrer" className="d-inline-block text-truncate align-middle" style={{ maxWidth: "100%" }}>
                                  {item.item_title || item.item_url}
                                </a>
                              ) : item.item_title || "-"}
                              {activeTab === "specified" && item.item_url && (
                                <details className="mt-1 small">
                                  <summary>URL 보기</summary>
                                  <div className="text-muted" style={{ overflowWrap: "anywhere", whiteSpace: "normal" }}>
                                    {item.item_url}
                                  </div>
                                  <button
                                    type="button"
                                    className="btn btn-outline-secondary btn-sm mt-1"
                                    onClick={async () => {
                                      try {
                                        await navigator.clipboard.writeText(item.item_url);
                                        setUrlCopyStatus({ itemId, success: true });
                                      } catch {
                                        setUrlCopyStatus({ itemId, success: false });
                                      }
                                    }}
                                  >
                                    URL 복사
                                  </button>
                                  {urlCopyStatus?.itemId === itemId && (
                                    <span className="ms-2" role="status">
                                      {urlCopyStatus.success ? "복사 완료" : "복사에 실패했습니다. URL을 선택해 복사해 주세요."}
                                    </span>
                                  )}
                                </details>
                              )}
                              {checkedSet.has(itemId) && (
                                <input
                                  type="text"
                                  className="form-control form-control-sm mt-1"
                                  aria-label="상품 메모"
                                  placeholder="메모 (선택)"
                                  value={itemCacheRef.current.get(itemId)?.memo ?? item.memo ?? ""}
                                  onChange={(event) => handleItemMemoChange(item, event.target.value)}
                                />
                              )}
                            </>
                          );
                        })()}
                      </td>
                      {compare_site == "danawa" && (
                        <td className="text-center">
                          {item.col == "left" && <span>왼쪽</span>}
                          {item.col == "rgt" && <span>오른쪽</span>}
                        </td>
                      )}
                      <td className="text-end text-nowrap">
                        {displayItemPrice(item) == null ? "-" : displayItemPrice(item)?.toLocaleString()}
                        {(compare_site === "direct" || typeof item.price_exposed_includes_shipping_fee === "boolean") && (
                          <small className="d-block text-muted">배송비 {
                            (compare_site === "direct" ? include_shipping_fee : item.price_exposed_includes_shipping_fee)
                              ? "포함" : "미포함"
                          }</small>
                        )}
                        {compare_site !== "direct" && item.item_price_source === "detail" && <small className="d-block text-muted">상세가</small>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {visibleItems && visibleItems.length === 0 && (
          <div className="table-responsive mt-3">
            <table className="table table-striped table-bordered">
              <thead>
                <tr>
                  <th scope="col"></th>
                  <th scope="col">판매자</th>
                  <th scope="col">상품명</th>
                  {compare_site == "danawa" && <th scope="col">컬럼</th>}
                  <th scope="col">가격</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td colSpan={100} className="text-center">
                    검색된 상품이 없습니다.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </>
    );
  };

  React.useEffect(() => {
    if (show) {
      void auth.refreshSubscriptionStatus();
      handleModalOpen();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  return (
    <Modal
      show={show}
      onHide={handleModalClose}
      backdrop="static"
      size="xl"
      scrollable={true}
    >
      <Modal.Header closeButton>
        <Modal.Title className="fs-5">상품 선택</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="row mb-2">
          <div className="col pe-0">
            <select className="form-control" disabled>
              {compare_site == "naver" && <option>네이버</option>}
              {compare_site == "danawa" && <option>다나와</option>}
              {compare_site == "enuri" && <option>에누리</option>}
              {compare_site == "direct" && <option>직접비교</option>}
            </select>
          </div>
          {compare_site == "danawa" && (
            <div className="col-auto pe-0">
              <select className="form-control" disabled>
                {compare_column == "left" ? <option>왼쪽</option> : null}
                {compare_column == "right" ? <option>오른쪽</option> : null}
                {compare_column == "all" ? <option>모두</option> : null}
              </select>
            </div>
          )}
          <div className="col-auto pe-0">
            <select className="form-control" disabled>
              {compare_range == "items_all" ? <option>전체상품</option> : null}
              {compare_range == "items_same_market" ? (
                <option>동일마켓</option>
              ) : null}
              {compare_range == "items_specified" ? (
                <option>지정상품</option>
              ) : null}
            </select>
          </div>
          {compare_site !== "direct" && <div className="col-auto">
            <select
              className="form-control"
              value={String(compare_pages || 1)}
              onChange={handleComparePagesChange}
            >
              <option value="1">1 페이지</option>
              <option value="2">2 페이지</option>
            </select>
          </div>}
        </div>
        {compare_site !== "direct" && <div className="row">
          <div className="col pe-0">
            <input
              type="text"
              className="form-control"
              value={compare_url}
              disabled
            />
          </div>
          <div className="col-auto">
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSearchSumbit}
              disabled={isSearching || processMessage !== "" || !!fetchingItemUrlTab}
            >
              검색
            </button>
          </div>
        </div>}

        {errorMessage && (
          <div
            className={`alert alert-danger alert-dismissible fade show mt-3`}
            role="alert"
          >
            {errorMessage}
            <button
              type="button"
              className="btn-close"
              aria-label="Close"
              onClick={() => setErrorMessage("")}
            ></button>
          </div>
        )}
        {processMessage && (
          <div className="alert alert-info mt-3" role="alert">
            {processMessage}
          </div>
        )}

        {compare_site !== "direct" && searchResultState === "stale" && !processMessage && (
          <div
            className="alert alert-warning border mt-3 mb-3"
            role="alert"
          >
            <AlertTriangle className="status-icon me-2" aria-hidden="true" />
            <small>
              검색 조건이 변경되었습니다. 현재 조건으로 반영하려면{" "}
              <strong>검색</strong> 버튼을 클릭하세요.
            </small>
          </div>
        )}

        {compare_site !== "direct" && searchResultState === "legacy" && !processMessage && (
          <div
            className="alert alert-warning border mt-3 mb-3"
            role="alert"
          >
            <AlertTriangle className="status-icon me-2" aria-hidden="true" />
            <small>
              이전 검색 결과입니다. 현재 조건의 결과를 확인하려면{" "}
              <strong>검색</strong> 버튼을 클릭하세요.
            </small>
          </div>
        )}

        {compare_site !== "direct" && searchResultState === "cached" &&
          items.length > 0 &&
          !processMessage && (
            <div
              className="alert alert-light border border-info mt-3 mb-3"
              role="alert"
            >
              <Info className="status-icon me-2 text-info" aria-hidden="true" />
              <small>
                마지막 검색 결과가 표시됩니다. 새로 검색하려면 위의{" "}
                <strong>검색</strong> 버튼을 클릭하세요.
              </small>
            </div>
          )}

        <Tabs
          id="compare-items-tabs"
          activeKey={activeTab}
          onSelect={(k) => k && setActiveTab(k)}
          className="mt-3 mb-3"
        >
          <Tab eventKey="my" title="내 상품 지정">
            {renderUrlSelector("my")}
            {renderItemsTable(myCheckedItems, 1)}
          </Tab>
          <Tab
            eventKey="specified"
            title={
              <span>
                경쟁상품 지정
                {!auth.subscriptionStatus?.item_is_premium && "(프로 전용)"}
              </span>
            }
            disabled={compare_range !== "items_specified"}
          >
            {renderUrlSelector("specified")}
            {renderItemsTable(specifiedCheckedItems, specifiedItemLimit)}
          </Tab>
          <Tab eventKey="prevented" title="경쟁방지상품 지정" disabled={compare_site === "direct"}>
            {compare_site !== "direct" && renderUrlSelector("prevented")}
            {renderItemsTable(preventedCheckedItems, 10)}
          </Tab>
        </Tabs>
      </Modal.Body>

      <Modal.Footer>
        <button
          type="button"
          className="btn btn-primary"
          disabled={isSearching || processMessage !== "" || !!fetchingItemUrlTab}
          onClick={handleSaveAll}
        >
          저장
        </button>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => handleModalClose()}
        >
          닫기
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default CompareItemSearchModal;
